<template>
  <a-modal
    :open="visible"
    title="QR 카테고리 관리"
    :width="600"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content">
      <!-- 카테고리 추가 폼 -->
      <div class="add-form">
        <a-input
          v-model:value="newCategory.name"
          placeholder="카테고리명을 입력하세요"
          style="flex: 1"
          @pressEnter="addCategory"
        />
        <a-input
          v-model:value="newCategory.description"
          placeholder="설명 (선택)"
          style="flex: 1"
        />
        <a-button type="primary" class="yellow-button" @click="addCategory" :loading="adding">
          추가
        </a-button>
      </div>

      <!-- 카테고리 목록 -->
      <div class="category-list">
        <a-table
          :columns="columns"
          :data-source="categories"
          :pagination="false"
          size="small"
          :row-key="record => record._id"
        >
          <template #name="{ record }">
            <div v-if="editingId === record._id" class="edit-cell">
              <a-input
                v-model:value="editData.name"
                size="small"
                @pressEnter="saveEdit(record)"
              />
            </div>
            <span v-else>{{ record.name }}</span>
          </template>

          <template #description="{ record }">
            <div v-if="editingId === record._id" class="edit-cell">
              <a-input
                v-model:value="editData.description"
                size="small"
              />
            </div>
            <span v-else class="text-muted">{{ record.description || '-' }}</span>
          </template>

          <template #qrCount="{ record }">
            <span>{{ record.qrCount || 0 }}</span>
          </template>

          <template #action="{ record }">
            <div class="action-buttons" v-if="editingId === record._id">
              <a-button type="link" size="small" @click="saveEdit(record)" :loading="saving">
                저장
              </a-button>
              <a-button type="link" size="small" @click="cancelEdit">
                취소
              </a-button>
            </div>
            <div class="action-buttons" v-else>
              <a-button type="link" size="small" @click="startEdit(record)">
                수정
              </a-button>
              <a-popconfirm
                title="이 카테고리를 삭제하시겠습니까?"
                ok-text="삭제"
                cancel-text="취소"
                @confirm="deleteCategory(record)"
              >
                <a-button type="link" size="small" danger>
                  삭제
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>

        <a-empty v-if="categories.length === 0" description="등록된 카테고리가 없습니다" />
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import QrService from '../service/QrService';

export default defineComponent({
  name: 'CategoryManageModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:visible', 'refresh'],
  setup(props, { emit }) {
    const adding = ref(false);
    const saving = ref(false);
    const editingId = ref(null);
    const newCategory = ref({ name: '', description: '' });
    const editData = ref({ name: '', description: '' });

    const columns = [
      {
        title: '카테고리명',
        key: 'name',
        width: 150,
        slots: { customRender: 'name' }
      },
      {
        title: '설명',
        key: 'description',
        slots: { customRender: 'description' }
      },
      {
        title: 'QR수',
        key: 'qrCount',
        width: 80,
        align: 'center',
        slots: { customRender: 'qrCount' }
      },
      {
        title: '',
        key: 'action',
        width: 120,
        align: 'center',
        slots: { customRender: 'action' }
      }
    ];

    watch(() => props.visible, (newVal) => {
      if (!newVal) {
        editingId.value = null;
        newCategory.value = { name: '', description: '' };
      }
    });

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const addCategory = async () => {
      if (!newCategory.value.name?.trim()) {
        message.warning('카테고리명을 입력해주세요.');
        return;
      }

      adding.value = true;
      try {
        const success = await QrService.createCategory({
          name: newCategory.value.name.trim(),
          description: newCategory.value.description?.trim() || ''
        });

        if (success) {
          newCategory.value = { name: '', description: '' };
          emit('refresh');
        }
      } finally {
        adding.value = false;
      }
    };

    const startEdit = (record) => {
      editingId.value = record._id;
      editData.value = {
        name: record.name,
        description: record.description || ''
      };
    };

    const cancelEdit = () => {
      editingId.value = null;
      editData.value = { name: '', description: '' };
    };

    const saveEdit = async (record) => {
      if (!editData.value.name?.trim()) {
        message.warning('카테고리명을 입력해주세요.');
        return;
      }

      saving.value = true;
      try {
        const success = await QrService.updateCategory(record._id, {
          name: editData.value.name.trim(),
          description: editData.value.description?.trim() || ''
        });

        if (success) {
          editingId.value = null;
          emit('refresh');
        }
      } finally {
        saving.value = false;
      }
    };

    const deleteCategory = async (record) => {
      const success = await QrService.deleteCategory(record._id);
      if (success) {
        emit('refresh');
      }
    };

    return {
      adding,
      saving,
      editingId,
      newCategory,
      editData,
      columns,
      handleCancel,
      addCategory,
      startEdit,
      cancelEdit,
      saveEdit,
      deleteCategory
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

.add-form {
  display: flex;
  gap: $spacing-sm;
  margin-bottom: $spacing-md;
  padding-bottom: $spacing-md;
  border-bottom: 1px solid $border-color;
}

.category-list {
  .edit-cell {
    padding-right: $spacing-xs;
  }

  .action-buttons {
    display: flex;
    gap: $spacing-xs;
    justify-content: center;
  }

  .text-muted {
    color: $text-muted;
    font-size: $font-size-sm;
  }
}

.yellow-button {
  background-color: $yellow-button-color;
  border-color: $yellow-button-color;
  color: $text-dark;

  &:hover {
    background-color: $yellow-button-color-hover;
    border-color: $yellow-button-color-hover;
  }
}
</style>
